export async function uploadToSignedUrl(input: {
  url: string;
  file: Blob;
  headers?: Record<string, string>;
  contentType?: string | null;
  onProgress?: (progressPercent: number) => void;
  signal?: AbortSignal;
}): Promise<string | null> {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    throw new Error('Upload unavailable while offline.');
  }

  return new Promise<string | null>((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('PUT', input.url, true);
    request.timeout = 120_000;
    const abort = () => request.abort();
    if (input.signal?.aborted) {
      reject(new DOMException('Upload cancelled', 'AbortError'));
      return;
    }
    input.signal?.addEventListener('abort', abort, { once: true });
    request.addEventListener('loadend', () =>
      input.signal?.removeEventListener('abort', abort),
    );
    request.addEventListener('timeout', () =>
      reject(new Error('Upload timed out.')),
    );

    const headers = input.headers ?? {};
    for (const [key, value] of Object.entries(headers)) {
      request.setRequestHeader(key, value);
    }

    if (input.contentType && !headers['Content-Type']) {
      request.setRequestHeader('Content-Type', input.contentType);
    }

    request.upload.addEventListener('progress', (event: ProgressEvent) => {
      if (!input.onProgress || !event.lengthComputable || event.total <= 0) {
        return;
      }

      input.onProgress(
        Math.min(100, Math.round((event.loaded / event.total) * 100)),
      );
    });

    request.addEventListener('load', () => {
      if (request.status >= 200 && request.status < 300) {
        input.onProgress?.(100);
        resolve(request.getResponseHeader('ETag'));
        return;
      }

      reject(
        new Error(
          `Upload failed with status ${request.status}${request.statusText ? ` ${request.statusText}` : ''}.`,
        ),
      );
    });

    request.addEventListener('error', () => {
      reject(
        new Error(
          typeof navigator !== 'undefined' && !navigator.onLine
            ? 'Upload unavailable while offline.'
            : 'Upload failed due to a network error.',
        ),
      );
    });

    request.addEventListener('abort', () => {
      reject(new Error('Upload was cancelled.'));
    });

    request.send(input.file);
  });
}
